import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent, VariantUpdateErrorEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import {
  buildMobileQuickAddUrl,
  isMobileQuickAddPicker,
  setQuickAddStatus,
  MOBILE_QUICK_ADD_SELECTOR,
} from '@theme/wc-quick-add-media';
import {
  clearSizeAlert,
  clearSizeSelection,
  installEasySellGuard,
  chosenSizeOptionValueId,
  readStoredSizeChoice,
  restoreSizeChoice,
  shouldRestoreSizeChoice,
  SIZE_RESTORED_EVENT,
  sizeChosenAfterSelection,
  writeStoredSizeChoice,
} from '@theme/wc-size-gate';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {AbortController} */
  #lifecycle = new AbortController();

  /** @type {string | undefined} */
  #lastMobileRequestUrl;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** @type {boolean} */
  #sizeChosen = true;

  /** @type {string | null} */
  #chosenVariantId = null;

  connectedCallback() {
    super.connectedCallback();
    this.#sizeChosen = this.dataset.wcSizeChosen !== 'false';
    if (!this.#sizeChosen) this.#restoreRememberedSize();
    if (!this.#sizeChosen) clearSizeSelection(this);
    installEasySellGuard(this.ownerDocument);
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
    this.#lifecycle = new AbortController();
    this.#bindMobileRetry();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#lifecycle.abort();
    this.#abortController?.abort();
  }

  #bindMobileRetry() {
    const root = this.closest(MOBILE_QUICK_ADD_SELECTOR);
    if (!root || root.dataset.wcQuickAddRetryBound === 'true') return;
    root.dataset.wcQuickAddRetryBound = 'true';
    root.addEventListener('click', this.#onMobileRetry, { signal: this.#lifecycle.signal });
  }

  /**
   * @param {Event} event
   */
  #onMobileRetry = (event) => {
    const retry = event.target instanceof Element ? event.target.closest('[data-wc-quick-add-retry]') : null;
    if (!retry) return;
    const root = this.closest(MOBILE_QUICK_ADD_SELECTOR);
    const url = this.#lastMobileRequestUrl || root?.dataset.wcLastRequestUrl;
    if (url) this.fetchUpdatedSection(url);
  };

  #failMobileQuickAddUpdate() {
    const root = this.closest(MOBILE_QUICK_ADD_SELECTOR);
    const message = root?.querySelector('[data-wc-quick-add-status]')?.getAttribute('data-error-text') || '';
    const source = this.querySelector('select option[selected], fieldset input:checked');
    const sourceId = source instanceof HTMLElement ? source.dataset.optionValueId || '' : '';
    setQuickAddStatus(root, message);
    this.dispatchEvent(new VariantUpdateErrorEvent(sourceId, { productId: this.dataset.productId ?? '' }));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    const optionName = event.target.closest?.('[data-option-name]')?.dataset?.optionName;
    if (sizeChosenAfterSelection(false, optionName)) this.#commitSizeChosen();

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (this.#sizeChosen && variantId) this.#rememberVariant(variantId);

    if (isOnProductPage) {
      if (this.#sizeChosen && variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if (isMobileQuickAddPicker(this)) {
      return buildMobileQuickAddUrl(productUrl, params);
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    const mainSectionId = this.#mainProductSectionId(selectedOption);
    if (mainSectionId) {
      return `${productUrl}?section_id=${mainSectionId}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Every consumer of a main product-page variant update (gallery, price, form, sticky bar,
   * inventory) lives inside this section, so a full-page render only adds header, footer
   * and app-embed bytes. Combined-listing switches still need `main`.
   * @param {HTMLElement} selectedOption - The selected option.
   * @returns {string | undefined} The section id to render, or undefined for a full page.
   */
  #mainProductSectionId(selectedOption) {
    if (this.dataset.templateProductMatch !== 'true' || !this.dataset.sectionId) return undefined;
    const connectedProductUrl = selectedOption.dataset.connectedProductUrl;
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    if (connectedProductUrl && connectedProductUrl !== currentUrl) return undefined;
    return this.dataset.sectionId;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {boolean} [isSelfHeal] - True when this is the single corrective re-fetch triggered by a server-side option auto-correction (prevents re-entrancy).
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, isSelfHeal = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    if (isMobileQuickAddPicker(this)) {
      this.#lastMobileRequestUrl = requestUrl;
      const root = this.closest(MOBILE_QUICK_ADD_SELECTOR);
      if (root) root.dataset.wcLastRequestUrl = requestUrl;
    }

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => {
        if (!response.ok && isMobileQuickAddPicker(this)) {
          throw new Error(`Failed to update product options: HTTP ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) {
          if (isMobileQuickAddPicker(this)) this.#failMobileQuickAddUpdate();
          return;
        }
        if (isMobileQuickAddPicker(this)) setQuickAddStatus(this.closest(MOBILE_QUICK_ADD_SELECTOR));

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
          this.#restoreSizeGate();
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
          this.#restoreSizeGate();
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }

          // Self-heal: the custom pack-first logic (in variant-main-picker.liquid) can
          // auto-select a different, VALID pack than the one we requested — e.g. switching
          // Colour to one that doesn't offer the carried-over pack. When that happens the
          // price/availability in THIS response were resolved from the impossible combo we
          // sent (no variant -> blank price + "Unavailable"). So if the selection the server
          // actually rendered differs from what we asked for, re-fetch ONCE with the server's
          // corrected option values and suppress this stale update. Scoped to the main product
          // picker; read straight from the response HTML so it doesn't depend on morph timing.
          if (!isSelfHeal && this.dataset.templateProductMatch === 'true') {
            const renderedOptionValues = this.#renderedOptionValueIds(html);
            if (renderedOptionValues.length && this.#optionValuesDiffer(requestUrl, renderedOptionValues)) {
              this.fetchUpdatedSection(
                this.#withOptionValues(requestUrl, renderedOptionValues),
                morphElementSelector,
                true
              );
              // Suppress the stale "Unavailable"/blank-price event; the corrective fetch dispatches the real one.
              return;
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        if (isMobileQuickAddPicker(this)) {
          this.#failMobileQuickAddUpdate();
          return;
        }
        console.error(error);
      });
  }

  /**
   * Reads the option-value ids the server actually rendered as selected, straight from a
   * fetched response document (independent of the live DOM / morph timing).
   * @param {Document} html - The parsed response document.
   * @returns {string[]} The selected option-value ids.
   */
  #renderedOptionValueIds(html) {
    const picker = html.querySelector(this.tagName.toLowerCase());
    if (!picker) return [];

    const selected = Array.from(picker.querySelectorAll('select option[selected], fieldset input[checked]'))
      .filter((el) => this.#sizeChosen || !el.closest('[data-option-name="size"]'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.optionValueId : undefined))
      .filter((/** @type {string | undefined} */ id) => Boolean(id));
    if (!this.#sizeChosen) return selected;
    const sizeId = chosenSizeOptionValueId(picker);
    if (sizeId && !selected.includes(sizeId)) selected.push(sizeId);
    return selected;
  }

  /**
   * Whether the option_values rendered by the server differ from those we requested.
   * @param {string} requestUrl - The request URL that was sent.
   * @param {string[]} renderedOptionValues - The option-value ids the response rendered as selected.
   * @returns {boolean}
   */
  #optionValuesDiffer(requestUrl, renderedOptionValues) {
    let requested;
    try {
      requested = new URL(requestUrl, window.location.origin).searchParams.get('option_values');
    } catch {
      return false;
    }
    if (!requested) return false;

    const requestedSorted = requested.split(',').filter(Boolean).sort().join(',');
    const renderedSorted = [...renderedOptionValues].sort().join(',');
    return requestedSorted !== renderedSorted;
  }

  /**
   * Returns the request URL with its option_values replaced by the corrected ids
   * (preserves base URL, section_id and any other params).
   * @param {string} requestUrl - The original request URL.
   * @param {string[]} optionValues - The corrected option-value ids.
   * @returns {string}
   */
  #withOptionValues(requestUrl, optionValues) {
    const url = new URL(requestUrl, window.location.origin);
    url.searchParams.set('option_values', optionValues.join(','));
    return url.toString();
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#restoreSizeGate();
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  #commitSizeChosen() {
    this.#sizeChosen = true;
    this.dataset.wcSizeChosen = 'true';
    clearSizeAlert(this);
    const sticky = this.closest('.shopify-section')?.querySelector('sticky-add-to-cart');
    if (sticky) sticky.dataset.wcSizeChosen = 'true';
  }

  /** @param {string | number} variantId */
  #rememberVariant(variantId) {
    this.#chosenVariantId = String(variantId);
    const storage = this.ownerDocument?.defaultView?.sessionStorage;
    writeStoredSizeChoice(storage, this.dataset.productId, variantId);
  }

  restoreRememberedSize() {
    this.#restoreRememberedSize();
  }

  #restoreRememberedSize() {
    const storage = this.ownerDocument?.defaultView?.sessionStorage;
    const stored = readStoredSizeChoice(storage, this.dataset.productId);
    const rendered = this.#renderedVariantId();
    if (!shouldRestoreSizeChoice(stored, rendered) || !restoreSizeChoice(this, rendered)) return;
    this.#sizeChosen = true;
    this.#chosenVariantId = String(rendered);
    const sticky = this.closest('.shopify-section')?.querySelector('sticky-add-to-cart');
    if (sticky) sticky.dataset.wcSizeChosen = 'true';
    this.dispatchEvent(new CustomEvent(SIZE_RESTORED_EVENT, { bubbles: true }));
  }

  /** @returns {string | null} */
  #renderedVariantId() {
    const root = this.closest('.shopify-section, dialog, [data-wc-quick-add-mobile]');
    const input = root?.querySelector('product-form-component input[name="id"]');
    return input instanceof HTMLInputElement && input.value ? input.value : null;
  }

  #restoreSizeGate() {
    this.dataset.wcSizeChosen = this.#sizeChosen ? 'true' : 'false';
    if (this.#sizeChosen) {
      if (this.#chosenVariantId) restoreSizeChoice(this, this.#chosenVariantId);
      clearSizeAlert(this);
      return;
    }
    clearSizeSelection(this);
  }

  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
